import type { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useGetSocial } from '@/application/services/settings.service'
import { useCurrentTheme } from '@/application/hooks/useCurrentTheme'
import { useConsentStore } from '@/application/stores/consent.store'
import { classNames } from '@/ui/utils/main'
import { XIcon, BlueskyIcon, FacebookIcon } from '@/ui/components/common/BrandIcons'
import type { SocialSettings } from '@/domains/types'

// ── Brand icons ───────────────────────────────────────────────────────────────
// X, Bluesky and Facebook live in `BrandIcons.tsx` — also used by the
// article `ShareButtons`. The rest are only ever rendered here.

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
    </svg>
  )
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

function TwitchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0 1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
  )
}

function DiscordIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  )
}

// ── Network config ────────────────────────────────────────────────────────────

type SocialUrlKey = keyof Omit<SocialSettings, 'footerLinks' | 'updatedAt'>

interface NetworkConfig {
  Icon: () => ReactElement
  label: string
  urlKey: SocialUrlKey
}

const NETWORK_CONFIG: Record<string, NetworkConfig | undefined> = {
  x: { Icon: XIcon, label: 'X', urlKey: 'xUrl' },
  bluesky: { Icon: BlueskyIcon, label: 'Bluesky', urlKey: 'blueskyUrl' },
  facebook: { Icon: FacebookIcon, label: 'Facebook', urlKey: 'facebookUrl' },
  instagram: { Icon: InstagramIcon, label: 'Instagram', urlKey: 'instagramUrl' },
  tiktok: { Icon: TikTokIcon, label: 'TikTok', urlKey: 'tiktokUrl' },
  youtube: { Icon: YouTubeIcon, label: 'YouTube', urlKey: 'youtubeUrl' },
  twitch: { Icon: TwitchIcon, label: 'Twitch', urlKey: 'twitchUrl' },
  discord: { Icon: DiscordIcon, label: 'Discord', urlKey: 'discordUrl' },
}

// ── Social link button ────────────────────────────────────────────────────────

interface SocialLinkProps {
  href: string
  icon: ReactNode
  label: string
}

function SocialLink({ href, icon, label }: SocialLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border bg-background text-foreground hover:bg-muted transition-colors"
    >
      {icon}
      {label}
    </a>
  )
}

// ── Footer ────────────────────────────────────────────────────────────────────

export function PublicFooter() {
  const { t } = useTranslation()
  const { data: social } = useGetSocial()
  const theme = useCurrentTheme()
  const year = new Date().getFullYear()
  const openConsentPreferences = useConsentStore((state) => state.openPreferences)

  const activeLinks =
    social?.footerLinks?.flatMap((networkKey) => {
      const config = NETWORK_CONFIG[networkKey]
      if (!config) return []
      const href = social[config.urlKey]
      if (!href) return []
      return [{ networkKey, config, href }]
    }) ?? []

  return (
    <footer className="border-t border-border bg-card mt-auto">
      <div className="container mx-auto px-4 py-4">
        {/*
         * Two arrangements, switched on the `desktop:` variant — a plain
         * 1024px width breakpoint (tailwind.config.js):
         *
         *  - At `desktop:`, a three-column row: logo | centre block | social.
         *  - Below it, three stacked rows in the same order, the social nav
         *    getting the footer's full width to wrap across.
         *
         * Below `desktop:`, the social nav gets the footer's full width rather
         * than a `flex-1` third of a row, which matters because six buttons in
         * a narrow `flex-1` column wrap onto several rows even though the
         * screen itself has plenty of width to spare — it's the narrowness of
         * the COLUMN, not of the screen, that causes wrapping. Giving the nav
         * the full stacked row instead puts all six on one line at any width
         * that would have shown the desktop layout, and lets it degrade to
         * wrapping only when the viewport itself is genuinely narrow (mobile
         * portrait).
         */}
        <div className="flex flex-col desktop:flex-row items-center gap-6">
          {/* Left column / first row — logo + copyright */}
          <div className="flex items-center gap-3 desktop:flex-1">
            <img src={theme.logoUrl} alt="" aria-hidden="true" className="h-9 w-9" />
            <div className="flex flex-col">
              <p className="text-xs text-muted-foreground">{t('footer.rights_year', { year })}</p>
              <p className="text-xs text-muted-foreground">{t('footer.rights_reserved')}</p>
            </div>
          </div>

          {/*
           * Centre — tagline + legal links stacked.
           *
           * At `desktop:` this is the middle column, and it must stay a direct
           * child of the flex row (rather than being pulled out as a sibling
           * above it): the row's height is driven by this, the tallest column,
           * and `items-center` on the row then vertically centres the logo and
           * the social buttons against that full height. Extracting it would
           * collapse the row and lose the centring.
           *
           * Below `desktop:` the same three children are simply laid out as
           * rows by `flex-col`, so the reflow needs no structural change and
           * the social nav stays a single `<nav>` — no second, hidden copy of
           * the markup to duplicate the links for assistive tech.
           */}
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm text-muted-foreground text-center">{t('footer.tagline')}</p>

            <nav
              aria-label={t('footer.legal.title')}
              className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground"
            >
              <Link
                to="/mentions-legales"
                className="hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
              >
                {t('footer.legal.mentions_legales')}
              </Link>
              <Link
                to="/confidentialite"
                className="hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
              >
                {t('footer.legal.confidentialite')}
              </Link>
              <Link
                to="/cookies"
                className="hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded"
              >
                {t('footer.legal.cookies')}
              </Link>
              <button
                type="button"
                onClick={openConsentPreferences}
                className="hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded underline underline-offset-2"
              >
                {t('footer.legal.manage_cookies')}
              </button>
            </nav>
          </div>

          {/*
           * Right column / third row — social buttons.
           *
           * `desktop:flex-1` (like the logo column) is what balances the row so
           * the centre block sits in the middle, and it is why this wrapper is
           * still rendered when there are no links: it doubles as the spacer
           * opposite the logo. That is a purely horizontal trick though — in the
           * stacked arrangement `flex-1` would instead grow the item along the
           * COLUMN axis, so both spacers are gated to `desktop:`. Below it the
           * parent's `items-center` makes this box shrink-to-fit and centres it,
           * which is what lets the nav take as much of the footer width as it
           * needs and wrap only when it runs out.
           *
           * When there are no links the wrapper collapses to `hidden` below
           * `desktop:` (1024px), because an empty flex item still costs a
           * `gap-6` — 24px of dead space under the legal links.
           */}
          <div
            className={classNames(
              'flex justify-center desktop:flex-1',
              activeLinks.length === 0 && 'hidden desktop:flex',
            )}
          >
            {activeLinks.length > 0 && (
              <nav
                aria-label={t('footer.social_networks')}
                className="flex items-center gap-2 flex-wrap justify-center"
              >
                {activeLinks.map(({ networkKey, config, href }) => {
                  const { Icon, label } = config
                  return <SocialLink key={networkKey} href={href} icon={<Icon />} label={label} />
                })}
              </nav>
            )}
          </div>
        </div>
      </div>
    </footer>
  )
}
